// Autosize композера: поле ввода растёт под набранный текст до потолка, дальше —
// внутренний скролл. Модуль чистый (DOM нет, замеры приходят числами) —
// тестируется в изоляции (autosize.test.mjs), как delay.ts и pacing.ts; в
// widget.ts остаются только чтения и записи стиля.
//
// Зачем здесь решение, а не безусловный пересчёт: сброс height в `auto` и
// следом чтение scrollHeight — это принудительный синхронный пересчёт вёрстки
// ВСЕЙ страницы хоста. На каждое нажатие клавиши он и даёт «недопустимо большую
// задержку отображения символа» (жалоба гостя №128, 18.08.2026): на тяжёлой
// чужой странице символ появляется заметно позже нажатия.

/** Замер поля перед пересчётом: длина набранного и ширина поля (перенос строк
 *  зависит от ширины, значит от неё зависит и высота). */
export interface ComposerMetrics {
  length: number;
  width: number;
}

/** Память с прошлого пересчёта: те же метрики плюс выставленная тогда высота. */
export interface ComposerMemo extends ComposerMetrics {
  height: number;
}

/** Память до первого пересчёта: ширина -1 не совпадёт ни с какой настоящей,
 *  поэтому самый первый вызов всегда идёт полным путём — со сбросом и записью. */
export const FRESH_COMPOSER: ComposerMemo = { length: -1, width: -1, height: -1 };

/** Нужен ли сброс высоты в `auto` перед замером.
 *
 *  Рост виден и без сброса: текст, не влезающий в текущую высоту, даёт
 *  scrollHeight больше неё. А вот сжаться поле само не умеет — при заданной
 *  height scrollHeight не покажет, что текста стало меньше. Поэтому дорогой
 *  сброс делаем только там, где высота МОГЛА уменьшиться: текст стал короче
 *  (удаление, отправка, очистка) или сменилась ширина (поворот экрана; открытие
 *  панели — у скрытого поля ширина 0).
 *
 *  @param memo что запомнили с прошлого пересчёта.
 *  @param now замер прямо сейчас. */
export function needsHeightReset(memo: ComposerMemo, now: ComposerMetrics): boolean {
  return now.length < memo.length || now.width !== memo.width;
}

/** Высота под содержимое: не выше потолка (дальше — внутренний скролл) и не
 *  отрицательная, каким бы ни пришёл замер скрытого поля.
 *
 *  @param scrollHeight замеренная высота содержимого.
 *  @param max потолок композера в px. */
export function fitComposerHeight(scrollHeight: number, max: number): number {
  if (!Number.isFinite(scrollHeight)) return 0;

  return Math.max(0, Math.min(Math.round(scrollHeight), max));
}

/** Писать ли высоту в стиль. Если сброса не было и высота не изменилась (а это
 *  набор внутри одной строки — самый частый случай), не пишем ничего: запись в
 *  стиль гасит вёрстку и заставляет браузер считать её заново.
 *
 *  @param memo что запомнили с прошлого пересчёта.
 *  @param height высота, посчитанная сейчас.
 *  @param reset высоту сбрасывали в `auto` — вернуть число обязаны. */
export function needsHeightWrite(memo: ComposerMemo, height: number, reset: boolean): boolean {
  return reset || height !== memo.height;
}
